import * as React from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  ClipboardPaste,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { cn, numero } from '@/lib/utils';
import { Botao, codigoClicavel } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Campo, Cartao, CorpoCartao, Esqueleto } from '@/componentes/ui/primitivos';
import { Segmentado } from '@/componentes/ui/segmentado';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { useDebounce } from '@/hooks/useDebounce';
import { DetalheTransferencia } from './DetalheTransferencia';
import { CampoProduto, type ProdutoEscolhido } from './CampoProduto';
import { ImportarLista, type ItemImportado, type RegistroImportacao } from './ImportarLista';
import { CampoLocalizador } from './Localizador';
import type { ConferenciaLocalizadores, PontaConferida } from './localizadoresCentros';

/**
 * Transferência entre CENTROS de estoque — ex.: VENDAS → RMA.
 *
 * É outro gesto que a transferência entre posições: ali a mercadoria muda de
 * prateleira dentro do mesmo centro; aqui ela muda de centro, o que no ERP
 * exige aprovação e move o kardex em duas pernas.
 *
 * QUEM CRIA NÃO APROVA
 * --------------------
 * A criação é de usuário comum e não mexe em estoque — gera uma pendência. A
 * aprovação move as duas pernas e pede permissão própria.
 *
 * Essa separação é NOSSA. No Procfit hoje é auto-aprovação: medindo 12 meses,
 * as mesmas pessoas criam e aprovam, nas mesmas proporções, e não há alçada
 * nenhuma configurada. O formulário existe, a segregação não.
 *
 * APROVAR FAZ AS DUAS PERNAS
 * --------------------------
 * No ERP, aprovar dá a saída e um segundo formulário dá a entrada — e entre os
 * dois o estoque não está em lugar nenhum. Aqui as duas acontecem na mesma
 * ação. Não é atalho: é o que impede o limbo, e o que impede o defeito que já
 * existe na base, onde uma transferência editada depois de aprovada saiu com
 * 1 item e entrou com 2, criando duas unidades do nada.
 */

type Aba = 'fila' | 'nova';

interface TransferenciaNaFila {
  transferencia: number;
  data: string | null;
  centroOrigem: number;
  centroOrigemNome: string | null;
  centroDestino: number;
  centroDestinoNome: string | null;
  observacao: string | null;
  itens: number;
  unidades: number;
  criadoPor: number | null;
  criadoPorNome: string | null;
  estado: 'pendente' | 'aprovada';
  aprovacao: number | null;
  aprovadoPor: string | null;
  aprovadoEm: string | null;
  /** Quantos localizadores distintos os itens usam; o endereço quando é um só. */
  localizadoresOrigem: number;
  localizadorOrigemEndereco: string | null;
  localizadoresDestino: number;
  localizadorDestinoEndereco: string | null;
  /** Só com o filtro de SKU: quanto daquele SKU a transferência leva. */
  quantidadeDoSku?: number | null;
}

/** Com SKU a lista é uma pesquisa, não a fila do dia: olha mais para trás. */
const DIAS_FILA = 30;
const DIAS_COM_SKU = 180;

interface ItemNovo {
  /** `null` até o usuário ESCOLHER da lista — digitar não basta. */
  produto: ProdutoEscolhido | null;
  quantidade: string;
  /** Código do localizador do item. Vazio = herda o padrão do cabeçalho. */
  localizadorOrigem: string;
  localizadorDestino: string;
}

const ITEM_VAZIO: ItemNovo = {
  produto: null,
  quantidade: '',
  localizadorOrigem: '',
  localizadorDestino: '',
};

/** O localizador da fila: o endereço, ou quantos são quando os itens variam. */
function resumoLocalizador(n: number, endereco: string | null): string {
  return endereco ?? (n > 1 ? `${n} localizadores` : '—');
}

function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function parado(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dias = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  return dias <= 0 ? 'hoje' : dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
}

export function TransferenciaCentros({
  empresa,
  centro,
}: {
  empresa: number | null;
  centro: number | null;
}): JSX.Element {
  const cliente = useQueryClient();
  const { temPermissao } = useSessao();
  const podeCriar = temPermissao('transferencia.criar');
  const podeAprovar = temPermissao('transferencia.aprovar');

  const [aba, definirAba] = React.useState<Aba>('fila');
  /*
    A transferência aberta vive na URL: é para cá que o hiperlink do kardex
    aponta, e um link que não sobrevive a um F5 não é link.
  */
  const [detalheUrl, definirDetalheUrl] = useParamUrl('transferencia');
  const detalhe = Number(detalheUrl) || null;
  const [confirmando, definirConfirmando] = React.useState<number | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const [aviso, definirAviso] = React.useState<string | null>(null);

  /*
    Filtro por SKU: na URL, para o voltar do navegador (e o link) trazerem a
    pesquisa de volta. O campo digita livre; a URL só muda depois da pausa.
  */
  const [skuUrl, definirSkuUrl] = useParamUrl('sku');
  const [skuDigitado, definirSkuDigitado] = React.useState(skuUrl);
  const skuAtrasado = useDebounce(skuDigitado, 400);
  React.useEffect(() => {
    if (skuAtrasado !== skuUrl) definirSkuUrl(skuAtrasado || null, { substituir: true });
    // Só a digitação manda na URL; a URL manda no campo pelo efeito abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skuAtrasado]);
  React.useEffect(() => {
    definirSkuDigitado((atual) => (atual === skuUrl ? atual : skuUrl));
  }, [skuUrl]);
  const skuFiltro = Number(skuUrl) > 0 ? Number(skuUrl) : null;
  const dias = skuFiltro === null ? DIAS_FILA : DIAS_COM_SKU;

  const fila = useQuery<{ transferencias: TransferenciaNaFila[] }>({
    queryKey: ['transferencias-centros', empresa, skuFiltro],
    queryFn: () =>
      api.get<{ transferencias: TransferenciaNaFila[] }>('/api/transferencias', {
        empresa,
        dias,
        ...(skuFiltro === null ? {} : { produto: skuFiltro }),
      }),
    enabled: empresa !== null,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const aprovar = useMutation({
    mutationFn: (transferencia: number) =>
      api.post<{ aprovacao: number; recebimento: number; itens: number }>(
        `/api/transferencias/${transferencia}/aprovar`,
        { empresa },
      ),
    onSuccess: async (r, transferencia) => {
      definirErro(null);
      definirConfirmando(null);
      definirAviso(
        `Transferência ${transferencia} aprovada: saída e entrada gravadas ` +
          `(aprovação ${r.aprovacao}, recebimento ${r.recebimento}).`,
      );
      await cliente.invalidateQueries({ queryKey: ['transferencias-centros'] });
    },
    onError: (e) => {
      definirConfirmando(null);
      definirErro(e instanceof Error ? e.message : 'Não foi possível aprovar.');
    },
  });

  const linhas = fila.data?.transferencias ?? [];
  const pendentes = linhas.filter((l) => l.estado === 'pendente');

  if (empresa === null) {
    return (
      <Cartao className="grid min-h-[240px] place-items-center px-6 text-center">
        <p className="text-[13px] text-textoFraco">Escolha uma empresa no seletor do topo.</p>
      </Cartao>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Segmentado
          tamanho="pequeno"
          valor={aba}
          aoMudar={definirAba}
          rotuloAcessivel="Transferência entre centros"
          opcoes={[
            { valor: 'fila', rotulo: 'A aprovar', contagem: pendentes.length },
            { valor: 'nova', rotulo: 'Nova transferência', icone: <Plus className="h-3.5 w-3.5" /> },
          ]}
        />
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          className="ml-auto"
          onClick={() => void fila.refetch()}
          carregando={fila.isFetching}
        >
          <RefreshCw className="h-4 w-4" />
          Atualizar
        </Botao>
      </div>

      {erro ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-perigo/40 bg-perigo/10 px-3 py-2.5 text-[13px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-perigo" />
          <span>{erro}</span>
        </div>
      ) : null}

      {aviso ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-sucesso/40 bg-sucesso/10 px-3 py-2.5 text-[13px] text-sucesso">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="font-semibold">{aviso}</span>
        </div>
      ) : null}

      <DetalheTransferencia transferencia={detalhe} aoFechar={() => definirDetalheUrl(null)} />

      {aba === 'nova' ? (
        <FormularioNova
          empresa={empresa}
          centro={centro}
          podeCriar={podeCriar}
          aoCriar={async (n) => {
            definirAviso(`Transferência ${n} criada. Aguardando aprovação.`);
            definirAba('fila');
            await cliente.invalidateQueries({ queryKey: ['transferencias-centros'] });
          }}
          aoErro={definirErro}
        />
      ) : (
        <Cartao className="relative flex min-h-0 flex-col">
          <div className="flex flex-wrap items-baseline gap-2 border-b border-borda px-4 py-2.5">
            <span className="text-[13px] font-bold">
              {skuFiltro === null ? `Últimos ${DIAS_FILA} dias` : `SKU ${skuFiltro} · últimos ${DIAS_COM_SKU} dias`}
            </span>
            <span className="tabular text-[11.5px] text-textoFraco">
              {fila.isLoading
                ? ''
                : `${numero(pendentes.length)} aguardando aprovação · ${numero(linhas.length)} no total`}
            </span>
            <span className="relative">
              <Campo
                value={skuDigitado}
                onChange={(e) => definirSkuDigitado(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                placeholder="Filtrar por SKU"
                title="Só as transferências que levam este SKU"
                aria-label="Filtrar por SKU"
                className="tabular h-[30px] w-[130px] pr-6 text-[12.5px] font-semibold"
              />
              {skuDigitado ? (
                <button
                  type="button"
                  onClick={() => {
                    definirSkuDigitado('');
                    definirSkuUrl(null, { substituir: true });
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[13px] text-textoFraco hover:text-texto"
                  aria-label="Limpar o filtro de SKU"
                  title="Limpar o filtro de SKU"
                >
                  ×
                </button>
              ) : null}
            </span>
            {fila.isFetching && !fila.isLoading ? (
              <span className="text-[11px] text-textoFraco">buscando…</span>
            ) : null}
            {!podeAprovar ? (
              <span className="ml-auto text-[11.5px] text-textoFraco">
                você não tem permissão para aprovar
              </span>
            ) : null}
          </div>

          <CorpoCartao className="min-h-0 flex-1 overflow-y-auto">
            {fila.isLoading ? (
              <Esqueleto className="h-64" />
            ) : fila.isError ? (
              <EstadoErro erro={fila.error} aoTentarDeNovo={() => void fila.refetch()} />
            ) : linhas.length === 0 ? (
              skuFiltro !== null ? (
                <EstadoVazio
                  icone={<BadgeCheck className="h-7 w-7" />}
                  titulo={`Nenhuma transferência com o SKU ${skuFiltro} nos últimos ${DIAS_COM_SKU} dias`}
                  detalhe="Confira o SKU ou limpe o filtro."
                />
              ) : (
                <EstadoVazio
                  icone={<BadgeCheck className="h-7 w-7" />}
                  titulo={`Nenhuma transferência nos últimos ${DIAS_FILA} dias`}
                  detalhe="Crie uma em “Nova transferência”."
                />
              )
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-borda text-textoFraco">
                    <th className="rotulo pb-1.5 pr-2">Criada</th>
                    <th className="rotulo pb-1.5 pr-2">Nº</th>
                    <th className="rotulo pb-1.5 pr-2">Origem → Destino</th>
                    <th className="rotulo pb-1.5 pr-2 text-right">Itens</th>
                    <th className="rotulo pb-1.5 pr-2">Criada por</th>
                    <th className="rotulo pb-1.5 pr-2">Estado</th>
                    <th className="pb-1.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-borda">
                  {linhas.map((l) => (
                    /*
                      Pendente recebe COR e uma barra na borda esquerda.
                      
                      A coluna "aguardando" no fim da linha ja dizia o estado,
                      mas exigia ler ate la, linha por linha. Numa fila, o que
                      falta fazer tem de saltar antes da leitura -- a barra
                      marca o bloco inteiro de pendencias de uma olhada so, e a
                      ordenacao do servidor garante que elas venham juntas no
                      topo.
                      
                      A linha em confirmacao continua ganhando destaque mais
                      forte: ela e a acao do momento e manda sobre o resto.
                    */
                    <tr
                      key={l.transferencia}
                      className={cn(
                        'border-l-[3px]',
                        confirmando === l.transferencia
                          ? 'border-l-atencao bg-atencao/10'
                          : l.estado === 'pendente'
                            ? 'border-l-atencao bg-atencao/[0.06]'
                            : 'border-l-transparent',
                      )}
                    >
                      <td className="whitespace-nowrap py-2 pr-2 text-[11.5px]">
                        <span className="tabular font-semibold">{dataCurta(l.data)}</span>
                        <span className="ml-1.5 text-textoFraco">{parado(l.data)}</span>
                      </td>
                      <td className="py-2 pr-2">
                        <button
                          type="button"
                          onClick={() => definirDetalheUrl(String(l.transferencia))}
                          className={cn(codigoClicavel, 'tabular font-mono text-[13px] font-bold')}
                        >
                          {l.transferencia}
                        </button>
                      </td>
                      <td className="py-2 pr-2 text-[12px]">
                        <span className="tabular font-mono font-bold">{l.centroOrigem}</span>
                        <ArrowRight className="mx-1 inline h-3 w-3 text-textoFraco" />
                        <span className="tabular font-mono font-bold">{l.centroDestino}</span>
                        <span
                          className="ml-1.5 block truncate text-[11px] text-textoFraco"
                          title={`${l.centroOrigemNome ?? ''} → ${l.centroDestinoNome ?? ''}`}
                        >
                          {l.centroOrigemNome ?? '—'} → {l.centroDestinoNome ?? '—'}
                        </span>
                        {l.localizadoresOrigem > 0 || l.localizadoresDestino > 0 ? (
                          <span
                            className="ml-1.5 block truncate font-mono text-[11px]"
                            title="Localizador de origem → de destino"
                          >
                            {resumoLocalizador(l.localizadoresOrigem, l.localizadorOrigemEndereco)}
                            <ArrowRight className="mx-1 inline h-3 w-3 text-textoFraco" />
                            {resumoLocalizador(l.localizadoresDestino, l.localizadorDestinoEndereco)}
                          </span>
                        ) : null}
                      </td>
                      <td className="tabular py-2 pr-2 text-right text-[12px]">
                        <strong>{numero(l.itens)}</strong>
                        <span className="text-textoFraco"> · {numero(l.unidades)} un</span>
                        {l.quantidadeDoSku != null ? (
                          <span
                            className="block text-[11px] font-semibold text-primaria"
                            title={`Quanto do SKU ${skuFiltro} esta transferência leva`}
                          >
                            SKU {skuFiltro}: {numero(l.quantidadeDoSku)} un
                          </span>
                        ) : null}
                      </td>
                      <td className="max-w-[160px] truncate py-2 pr-2 text-[11.5px]">
                        {l.criadoPorNome ?? l.criadoPor ?? '—'}
                      </td>
                      <td className="py-2 pr-2 text-[11.5px]">
                        {l.estado === 'aprovada' ? (
                          <span className="inline-flex items-center gap-1 font-semibold text-sucesso">
                            <CheckCircle2 className="h-3 w-3" />
                            aprovada
                          </span>
                        ) : (
                          <span className="font-semibold text-atencao">aguardando</span>
                        )}
                        {l.aprovadoPor ? (
                          <span className="block text-[10.5px] text-textoFraco">
                            {l.aprovadoPor} · {dataCurta(l.aprovadoEm)}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 text-right">
                        {l.estado !== 'pendente' || !podeAprovar ? null : confirmando ===
                          l.transferencia ? (
                          <span className="inline-flex items-center gap-1.5">
                            {/* Aprovar move estoque nas duas pontas e não tem desfazer. */}
                            <span className="text-[11px] font-semibold text-atencao">
                              Move o estoque. Confirma?
                            </span>
                            <Botao
                              variante="primaria"
                              tamanho="pequeno"
                              carregando={aprovar.isPending}
                              onClick={() => aprovar.mutate(l.transferencia)}
                            >
                              Aprovar
                            </Botao>
                            <Botao
                              variante="fantasma"
                              tamanho="pequeno"
                              onClick={() => definirConfirmando(null)}
                            >
                              Cancelar
                            </Botao>
                          </span>
                        ) : (
                          <Botao
                            variante="secundaria"
                            tamanho="pequeno"
                            onClick={() => {
                              definirErro(null);
                              definirConfirmando(l.transferencia);
                            }}
                          >
                            <BadgeCheck className="h-3.5 w-3.5" />
                            Aprovar
                          </Botao>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CorpoCartao>
        </Cartao>
      )}
    </div>
  );
}

interface CentroOpcao {
  centroEstoque: number;
  nome: string;
  empresa: number;
}

function FormularioNova({
  empresa,
  centro,
  podeCriar,
  aoCriar,
  aoErro,
}: {
  empresa: number;
  centro: number | null;
  podeCriar: boolean;
  aoCriar: (transferencia: number) => void | Promise<void>;
  aoErro: (mensagem: string) => void;
}): JSX.Element {
  const [origem, definirOrigem] = React.useState<string>(centro === null ? '' : String(centro));
  const [destino, definirDestino] = React.useState('');
  const [observacao, definirObservacao] = React.useState('');
  const [itens, definirItens] = React.useState<ItemNovo[]>([ITEM_VAZIO]);
  /** Os localizadores PADRÃO — o item que não traz o seu herda. */
  const [locOrigem, definirLocOrigem] = React.useState('');
  const [locDestino, definirLocDestino] = React.useState('');
  const [colando, definirColando] = React.useState(false);
  /** O rastro das listas coladas — vai junto na criação, para a auditoria. */
  const [importacao, definirImportacao] = React.useState<RegistroImportacao | null>(null);

  /*
   * O que veio da lista entra na MESMA lista de itens: soma no produto que já
   * estava lá, ocupa o lugar das linhas em branco. Daqui em diante é editável
   * como qualquer item digitado.
   */
  const receberImportados = (novos: ItemImportado[], registro: RegistroImportacao): void => {
    definirItens((atuais) => {
      const lista = atuais.filter((i) => i.produto !== null);
      for (const n of novos) {
        const origemN = n.localizadorOrigem ?? '';
        const destinoN = n.localizadorDestino ?? '';
        // Soma só no MESMO produto saindo e entrando dos mesmos localizadores.
        const j = lista.findIndex(
          (i) =>
            i.produto!.produto === n.produto.produto &&
            i.localizadorOrigem === origemN &&
            i.localizadorDestino === destinoN,
        );
        if (j >= 0) {
          const x = lista[j]!;
          lista[j] = { ...x, quantidade: String((Number(x.quantidade) || 0) + n.quantidade) };
        } else {
          lista.push({
            produto: n.produto,
            quantidade: String(n.quantidade),
            localizadorOrigem: origemN,
            localizadorDestino: destinoN,
          });
        }
      }
      return lista.length > 0 ? lista : [ITEM_VAZIO];
    });
    // Duas listas coladas na mesma transferência: as duas ficam no rastro.
    definirImportacao((a) =>
      a === null
        ? registro
        : {
            arquivo: [a.arquivo, registro.arquivo].filter(Boolean).join(', ') || null,
            texto: `${a.texto}
--- nova lista ---
${registro.texto}`,
            linhas: [...a.linhas, ...registro.linhas],
          },
    );
    definirColando(false);
  };

  const contexto = useQuery<{ centros: CentroOpcao[] }>({
    queryKey: ['contexto'],
    queryFn: () => api.get<{ centros: CentroOpcao[] }>('/api/contexto'),
    staleTime: 30 * 60_000,
  });

  /*
   * DOIS TIPOS DE TRANSFERÊNCIA, e a diferença não é de tela.
   *
   * "Entre centros" move mercadoria dentro da MESMA empresa — VENDAS para
   * RMA, por exemplo. É o caso do dia a dia e o padrão.
   *
   * "Entre empresas" atravessa a fronteira: filial 15 para filial 22. O ERP
   * permite (681 casos em 12 meses, um deles criado hoje) e grava `EMPRESA` e
   * `FILIAL` com a empresa da ORIGEM — conferido, é assim em todas. Mas a
   * mercadoria troca de dono, então pedimos o gesto explícito em vez de
   * simplesmente listar todos os centros no destino: um destino que aceita
   * qualquer empresa em silêncio é exatamente como o saldo nasce no lugar
   * errado.
   *
   * A ORIGEM nunca sai da empresa da sessão: é dela que a coisa sai.
   *
   * O destino só oferece centro que ESTE usuário já enxerga — `/api/contexto`
   * devolve só os liberados —, que é a regra de "ter as duas filiais
   * liberadas". O servidor confere de novo; aqui a lista só evita oferecer
   * porta fechada.
   */
  const [modo, definirModo] = React.useState<'centros' | 'empresas'>('centros');
  const entreEmpresas = modo === 'empresas';

  const todos = contexto.data?.centros ?? [];
  const daEmpresa = todos.filter((c) => Number(c.empresa) === empresa);
  const centros = daEmpresa;
  const centrosDestino = entreEmpresas ? todos : daEmpresa;

  // Trocar de modo não pode deixar um destino de outra empresa selecionado
  // para trás: o servidor recusaria, mas só depois de a pessoa preencher tudo.
  React.useEffect(() => {
    if (entreEmpresas) return;
    definirDestino((d) =>
      d !== '' && !daEmpresa.some((c) => String(c.centroEstoque) === d) ? '' : d,
    );
    // `daEmpresa` é derivado da consulta; o modo é o que de fato dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entreEmpresas]);

  /*
   * OS LOCALIZADORES — conferidos no servidor a cada mudança, com a mesma
   * régua da criação (`/api/transferencias/conferir-localizadores`): existe,
   * é do centro certo (faltar não trava desde 30/10/2026, só avisa). Volta
   * também o saldo do produto no localizador de origem.
   *
   * O pedido vai como texto para o atraso: um objeto novo a cada render
   * reiniciaria o relógio e a conferência nunca sairia.
   */
  const pedidoTexto = JSON.stringify({
    centroOrigem: origem === '' ? null : Number(origem),
    centroDestino: destino === '' ? null : Number(destino),
    padroes: { origem: locOrigem.trim(), destino: locDestino.trim() },
    linhas: itens.map((i, n) => ({
      chave: n,
      produto: i.produto?.produto ?? null,
      origem: i.localizadorOrigem.trim(),
      destino: i.localizadorDestino.trim(),
      quantidade: Number(i.quantidade) || 0,
    })),
  });
  const pedidoAtrasado = useDebounce(pedidoTexto, 350);
  const pedido = React.useMemo(
    () =>
      JSON.parse(pedidoAtrasado) as {
        centroOrigem: number | null;
        centroDestino: number | null;
        padroes: { origem: string; destino: string };
      },
    [pedidoAtrasado],
  );
  const conferirLigado =
    pedido.centroOrigem !== null &&
    pedido.centroDestino !== null &&
    pedido.centroOrigem !== pedido.centroDestino;
  const localizadores = useQuery<ConferenciaLocalizadores>({
    queryKey: ['transferencia-conferir-localizadores', 'formulario', pedidoAtrasado],
    queryFn: () =>
      api.post<ConferenciaLocalizadores>(
        '/api/transferencias/conferir-localizadores',
        JSON.parse(pedidoAtrasado) as unknown,
      ),
    enabled: conferirLigado,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
  const conferindo =
    pedidoTexto !== pedidoAtrasado ||
    (conferirLigado && (localizadores.isFetching || localizadores.isPlaceholderData));
  const conferencia = conferirLigado ? localizadores.data : undefined;
  const exige = conferencia?.exige ?? { origem: false, destino: false };

  const criar = useMutation({
    mutationFn: () =>
      api.post<{ transferencia: number }>('/api/transferencias', {
        empresa,
        centroOrigem: Number(origem),
        centroDestino: Number(destino),
        entreEmpresas,
        observacao,
        localizadorOrigemPadrao: locOrigem.trim() || null,
        localizadorDestinoPadrao: locDestino.trim() || null,
        itens: itens
          .filter((i) => i.produto !== null && Number(i.quantidade) > 0)
          .map((i) => ({
            produto: i.produto!.produto,
            quantidade: Number(i.quantidade),
            localizadorOrigem: i.localizadorOrigem.trim() || null,
            localizadorDestino: i.localizadorDestino.trim() || null,
          })),
        importacao,
      }),
    onSuccess: (r) => {
      definirItens([ITEM_VAZIO]);
      definirObservacao('');
      definirImportacao(null);
      definirColando(false);
      void aoCriar(r.transferencia);
    },
    onError: (e) => aoErro(e instanceof Error ? e.message : 'Não foi possível criar.'),
  });

  const validos = itens.filter((i) => i.produto !== null && Number(i.quantidade) > 0);
  /*
   * Item válido com localizador que não serve — inexistente ou de outro
   * centro. O servidor recusa do mesmo jeito; aqui
   * só evita o clique perdido.
   */
  const semLocalizador = itens.filter((i, n) => {
    if (i.produto === null || !(Number(i.quantidade) > 0)) return false;
    const l = conferencia?.linhas.find((x) => x.chave === n);
    return !l || l.origem.erro !== null || l.destino.erro !== null;
  }).length;
  const padraoRuim =
    typeof conferencia?.padroes.origem === 'string' ||
    typeof conferencia?.padroes.destino === 'string';
  const pronto =
    podeCriar &&
    origem !== '' &&
    destino !== '' &&
    origem !== destino &&
    validos.length > 0 &&
    !conferindo &&
    !localizadores.isError &&
    !padraoRuim &&
    semLocalizador === 0;
  const dicaLocalizador = (exigido: boolean): string =>
    destino === '' || origem === ''
      ? 'escolha os centros primeiro'
      : exigido
        ? 'opcional — o centro usa localizador; sem ele, vai sem local'
        : 'opcional — este centro não usa localizador';

  return (
    <Cartao>
      <CorpoCartao className="space-y-3">
        {!podeCriar ? (
          <div className="rounded-xl border border-atencao/40 bg-atencao/10 px-3 py-2.5 text-[13px]">
            Você não tem permissão para criar transferência.
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Segmentado
            tamanho="pequeno"
            valor={modo}
            aoMudar={definirModo}
            rotuloAcessivel="Tipo de transferência"
            opcoes={[
              { valor: 'centros', rotulo: 'Entre centros' },
              { valor: 'empresas', rotulo: 'Entre empresas' },
            ]}
          />
          <span className="text-[11.5px] text-textoFraco">
            {entreEmpresas
              ? 'O destino pode ser de outra empresa. A transferência fica registrada na empresa da origem.'
              : `Origem e destino da empresa ${empresa}.`}
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="rotulo">Centro de origem</span>
            <select
              value={origem}
              onChange={(e) => definirOrigem(e.target.value)}
              className="mt-1 min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-2 text-[13px]"
            >
              <option value="">selecione…</option>
              {centros.map((c) => (
                <option key={c.centroEstoque} value={c.centroEstoque}>
                  {c.centroEstoque} · {c.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="rotulo">Centro de destino</span>
            <select
              value={destino}
              onChange={(e) => definirDestino(e.target.value)}
              className="mt-1 min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-2 text-[13px]"
            >
              <option value="">selecione…</option>
              {centrosDestino
                .filter((c) => String(c.centroEstoque) !== origem)
                .map((c) => (
                  <option key={c.centroEstoque} value={c.centroEstoque}>
                    {c.centroEstoque} · {c.nome}
                    {Number(c.empresa) !== empresa ? ` — empresa ${c.empresa}` : ''}
                  </option>
                ))}
            </select>
          </label>
        </div>

        {/*
          Os localizadores PADRÃO, em rota (sai de → entra em), como na
          transferência entre localizadores. O item que não traz o seu herda
          estes; é o que o Procfit grava no cabeçalho.
        */}
        <div className="grid items-stretch gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <CampoLocalizador
            id="transf-centros-loc-origem"
            ponta="origem"
            rotulo="Localizador de origem (padrão)"
            dica={dicaLocalizador(exige.origem)}
            valor={locOrigem}
            aoMudar={definirLocOrigem}
            resolvido={
              locOrigem.trim() === pedido.padroes.origem
                ? (conferencia?.padroes.origem ?? null)
                : null
            }
            carregando={locOrigem.trim() !== '' && conferindo}
          />
          <div className="flex items-center justify-center">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-primaria text-white shadow-xs">
              <ArrowRight className="h-5 w-5 rotate-90 sm:rotate-0" />
            </span>
          </div>
          <CampoLocalizador
            id="transf-centros-loc-destino"
            ponta="destino"
            rotulo="Localizador de destino (padrão)"
            dica={dicaLocalizador(exige.destino)}
            valor={locDestino}
            aoMudar={definirLocDestino}
            resolvido={
              locDestino.trim() === pedido.padroes.destino
                ? (conferencia?.padroes.destino ?? null)
                : null
            }
            carregando={locDestino.trim() !== '' && conferindo}
          />
        </div>

        <label className="block">
          <span className="rotulo">Observação</span>
          <input
            value={observacao}
            onChange={(e) => definirObservacao(e.target.value)}
            placeholder="motivo da transferência"
            className="mt-1 min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-3 text-[13px]"
          />
        </label>

        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <span className="rotulo">Produtos</span>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => definirItens((a) => [...a, ITEM_VAZIO])}
            >
              <Plus className="h-3.5 w-3.5" />
              Adicionar
            </Botao>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => definirColando((a) => !a)}
              aria-expanded={colando}
            >
              <ClipboardPaste className="h-3.5 w-3.5" />
              Colar lista
            </Botao>
            {importacao ? (
              <span className="text-[11.5px] text-textoFraco">
                {importacao.linhas.length} linha(s) importada(s) — fica registrado
              </span>
            ) : null}
          </div>
          {colando ? (
            <div className="mb-2">
              <ImportarLista
                centroOrigem={origem === '' ? null : Number(origem)}
                centroDestino={destino === '' ? null : Number(destino)}
                padroes={{ origem: locOrigem.trim(), destino: locDestino.trim() }}
                aoAdicionar={receberImportados}
                aoFechar={() => definirColando(false)}
              />
            </div>
          ) : null}
          <ul className="space-y-2">
            {itens.map((item, i) => (
              <li key={i} className="space-y-1">
                <div className="flex items-center gap-2">
                  <CampoProduto
                    valor={item.produto}
                    rotuloAcessivel={`Produto da linha ${i + 1}`}
                    aoEscolher={(p) =>
                      definirItens((a) => a.map((x, j) => (j === i ? { ...x, produto: p } : x)))
                    }
                    aoLimpar={() =>
                      definirItens((a) => a.map((x, j) => (j === i ? { ...x, produto: null } : x)))
                    }
                  />
                  <input
                    value={item.quantidade}
                    onChange={(e) =>
                      definirItens((a) =>
                        a.map((x, j) =>
                          j === i ? { ...x, quantidade: e.target.value.replace(/\D/g, '') } : x,
                        ),
                      )
                    }
                    inputMode="numeric"
                    placeholder="qtde"
                    aria-label={`Quantidade da linha ${i + 1}`}
                    className="tabular min-h-[36px] w-[90px] rounded-lg border border-borda bg-superficie px-2 text-center font-mono text-[13px]"
                  />
                  {itens.length > 1 ? (
                    <Botao
                      variante="fantasma"
                      tamanho="pequeno"
                      onClick={() => definirItens((a) => a.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Botao>
                  ) : null}
                </div>
                {/*
                  Os localizadores do item, compactos: vazio herda o padrão.
                  Ao lado, o que vale (endereço) e o saldo do produto nele.
                */}
                <LocalizadoresDoItem
                  linha={i + 1}
                  item={item}
                  conferida={
                    conferindo ? null : (conferencia?.linhas.find((x) => x.chave === i) ?? null)
                  }
                  padraoOrigem={locOrigem.trim()}
                  padraoDestino={locDestino.trim()}
                  aoMudar={(campo, v) =>
                    definirItens((a) => a.map((x, j) => (j === i ? { ...x, [campo]: v } : x)))
                  }
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-2 border-t border-borda pt-3">
          {semLocalizador > 0 && !conferindo && conferencia ? (
            <span className="text-[11.5px] font-semibold text-perigo">
              {semLocalizador} item(ns) sem localizador válido — informe no item ou no padrão
            </span>
          ) : localizadores.isError ? (
            <span className="text-[11.5px] text-perigo">
              Não foi possível conferir os localizadores:{' '}
              {localizadores.error instanceof Error ? localizadores.error.message : 'erro'}
            </span>
          ) : (
            <span className="text-[11.5px] text-textoFraco">
              Criar não move estoque — gera a pendência para aprovação.
            </span>
          )}
          <Botao
            variante="primaria"
            tamanho="pequeno"
            className="ml-auto"
            disabled={!pronto}
            carregando={criar.isPending}
            onClick={() => criar.mutate()}
          >
            Criar transferência
          </Botao>
        </div>
      </CorpoCartao>
    </Cartao>
  );
}

/**
 * A linha de localizadores de um item: dois campos curtos (o código) e, ao
 * lado de cada um, o que vale — o endereço achado, marcado "padrão" quando
 * herdado, ou o motivo de não servir. Na origem, o saldo do produto ali.
 */
function LocalizadoresDoItem({
  linha,
  item,
  conferida,
  padraoOrigem,
  padraoDestino,
  aoMudar,
}: {
  linha: number;
  item: ItemNovo;
  conferida: ConferenciaLocalizadores['linhas'][number] | null;
  padraoOrigem: string;
  padraoDestino: string;
  aoMudar: (campo: 'localizadorOrigem' | 'localizadorDestino', valor: string) => void;
}): JSX.Element {
  const saldo =
    conferida && conferida.saldoOrigem !== null ? (
      <span
        className={cn(
          'ml-1.5 font-sans',
          conferida.totalNaOrigem > conferida.saldoOrigem
            ? 'font-bold text-atencao'
            : 'text-textoFraco',
        )}
        title={
          conferida.totalNaOrigem > conferida.saldoOrigem
            ? `Saldo do produto neste localizador; a lista tira ${conferida.totalNaOrigem}. ` +
              'Não impede criar — a aprovação confere.'
            : 'Saldo do produto neste localizador'
        }
      >
        saldo {numero(conferida.saldoOrigem)}
      </span>
    ) : null;
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-1 text-[11.5px]">
      <PontaDoItem
        rotulo="Sai de"
        aria={`Localizador de origem da linha ${linha}`}
        valor={item.localizadorOrigem}
        padrao={padraoOrigem}
        conferida={conferida?.origem ?? null}
        aoMudar={(v) => aoMudar('localizadorOrigem', v)}
        extra={saldo}
      />
      <ArrowRight className="h-3 w-3 shrink-0 text-textoFraco" />
      <PontaDoItem
        rotulo="Entra em"
        aria={`Localizador de destino da linha ${linha}`}
        valor={item.localizadorDestino}
        padrao={padraoDestino}
        conferida={conferida?.destino ?? null}
        aoMudar={(v) => aoMudar('localizadorDestino', v)}
      />
    </div>
  );
}

function PontaDoItem({
  rotulo,
  aria,
  valor,
  padrao,
  conferida,
  aoMudar,
  extra,
}: {
  rotulo: string;
  aria: string;
  valor: string;
  padrao: string;
  conferida: PontaConferida | null;
  aoMudar: (v: string) => void;
  extra?: React.ReactNode;
}): JSX.Element {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="text-textoFraco">{rotulo}</span>
      <input
        value={valor}
        onChange={(e) => aoMudar(e.target.value.replace(/\D/g, ''))}
        inputMode="numeric"
        placeholder={padrao || 'código'}
        title={padrao ? `Vazio = o padrão (${padrao})` : 'Código do localizador'}
        aria-label={aria}
        className={cn(
          'tabular h-7 w-[76px] rounded-md border bg-superficie px-1.5 text-center font-mono text-[12px] focus:border-primaria focus:outline-none',
          conferida?.erro && !conferida.herdada ? 'border-perigo' : 'border-borda',
        )}
      />
      {conferida?.posicao ? (
        <span className="max-w-[220px] truncate font-mono font-bold" title={conferida.posicao.endereco}>
          {conferida.posicao.endereco}
          {conferida.herdada ? (
            <span className="ml-1 font-sans text-[10.5px] font-normal text-textoFraco">padrão</span>
          ) : null}
          {!conferida.posicao.ativo ? (
            <span className="ml-1 font-sans text-[10.5px] font-semibold text-atencao">inativo</span>
          ) : null}
          {extra}
        </span>
      ) : conferida?.erro ? (
        <span className="text-perigo">{conferida.erro}</span>
      ) : null}
    </span>
  );
}
